import { useRouter, useRoute, RouteLocationRaw } from "vue-router";
import { getLocaleFromPath, localizePath } from "@/plugins/localizedRoutes";

/**
 * Composable that provides localized navigation methods
 */
export function useLocalizedNavigation() {
  const router = useRouter();
  const route = useRoute();

  /**
   * Navigate to a localized route
   * Similar to router.push but automatically adds current locale prefix
   */
  const navigateTo = (to: RouteLocationRaw) => {
    const currentLocale = getLocaleFromPath(route.path);

    // If it's a string path
    if (typeof to === "string") {
      return router.push(localizePath(to, currentLocale));
    }

    // If it's an object
    const localizedTo = { ...to };

    // Handle path-based navigation
    if ("path" in localizedTo && localizedTo.path) {
      localizedTo.path = localizePath(localizedTo.path, currentLocale);
    }
    // Handle named routes
    else if ("name" in localizedTo && localizedTo.name) {
      const routeName = String(localizedTo.name);
      if (!routeName.startsWith(`${currentLocale}-`)) {
        localizedTo.name = `${currentLocale}-${routeName}`;
      }
    }

    return router.push(localizedTo);
  };

  /**
   * Replace current route with a localized route
   * Similar to router.replace but automatically adds current locale prefix
   */
  const replaceTo = (to: RouteLocationRaw) => {
    const currentLocale = getLocaleFromPath(route.path);

    // If it's a string path
    if (typeof to === "string") {
      return router.replace(localizePath(to, currentLocale));
    }

    // If it's an object
    const localizedTo = { ...to };

    // Handle path-based navigation
    if ("path" in localizedTo && localizedTo.path) {
      localizedTo.path = localizePath(localizedTo.path, currentLocale);
    }
    // Handle named routes
    else if ("name" in localizedTo && localizedTo.name) {
      const routeName = String(localizedTo.name);
      if (!routeName.startsWith(`${currentLocale}-`)) {
        localizedTo.name = `${currentLocale}-${routeName}`;
      }
    }

    return router.replace(localizedTo);
  };

  /**
   * Navigate to the same route but with a different locale
   */
  const switchLocale = (locale: string) => {
    // Get the current path without locale
    const pathWithoutLocale = route.path.split("/").slice(2).join("/");

    // Create new path with the selected locale
    const newPath = `/${locale}/${pathWithoutLocale}`;

    // Navigate to the new localized path
    return router.push(newPath);
  };

  return {
    navigateTo,
    replaceTo,
    switchLocale,
  };
}
